import { Injectable, Inject, DOCUMENT } from '@angular/core'
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router'
import { Title, Meta } from '@angular/platform-browser'
import { filter } from 'rxjs'
import { injectLogger } from '@/services/logger.service'
import { TranslocoService } from '@jsverse/transloco'
import { BettingSiteConfig, SiteConfigService } from './site-config.service'

export interface SeoData {
  title?: string;
  description?: string;
  keywords?: string[];
  canonicalUrl?: string;
  jsonLd?: object;
}

@Injectable({ providedIn: 'root' })
export class SEOService {
	public defaultTitle = ''
	private readonly logger = injectLogger('SEOService')

	constructor(
    private title: Title,
    private meta: Meta,
		private router: Router,
    private siteConfigService: SiteConfigService,
		private translocoService: TranslocoService,
    @Inject(DOCUMENT) private document: Document
	) {}

	/**
	 * Initializes the SEO service to automatically update page title and SEO on route changes.
	 * Subscribes to Angular Router navigation events, retrieves the deepest child route,
	 * and sets the page SEO using data from the route's `data` property.
	 */
	public init(siteConfig: BettingSiteConfig): void {
		this.defaultTitle = siteConfig.footerConfig.copyright || ''
		this.router.events
			.pipe(filter((event) => event instanceof NavigationEnd))
			.subscribe(() => {
				const route = this.getDeepestChild(this.router.routerState.root)
				const data = route.snapshot.data
				this.setPageSeo({
					title: data['title'] ? this.translocoService.translate(data['title']) : undefined,
					description: data['description'] || undefined,
					keywords: data['keywords'] || undefined,
					canonicalUrl: data['canonicalUrl'] || undefined,
					jsonLd: data['jsonLd'] || undefined
				})
			})
	}

	/**
   * Applies all SEO updates at once.
   */
	public setPageSeo(seoData: SeoData): void {
		this.setTitle(seoData.title)
		this.setDescription(seoData.description)
		this.setKeywords(seoData.keywords)
		this.setCanonicalUrl(seoData.canonicalUrl)
		this.setJsonLd(seoData.jsonLd)
		this.logger.debug('Updated:', seoData)
	}

	/**
   * Set the title of the current HTML document. If title is not present it will
	 * set the default title as `this.defaultTitle`.
	 * @example <title>Example title | Default</title>
	 * @example <title>Default</title>
   */
	private setTitle(pageTitle?: string): void {
		const title = pageTitle ? `${pageTitle} | ${this.defaultTitle}` : this.defaultTitle
		this.title.setTitle(title)
	}

	/**
   * Sets or removed the meta description tag.
	 * @example <meta name="description" content="...">.
   */
	private setDescription(description?: string): void {
		const selector = 'meta[name="description"]'
		if (description) {
			this.meta.updateTag({ name: 'description', content: description })
		} else {
			const tag = this.document.querySelector<HTMLMetaElement>(selector)
			if (tag) tag.remove()
		}
	}

	/**
   * Sets or removes the meta keywords tag from a string array.
	 * @example <meta name="keywords" content="keyword 1, keyword 2">
   */
	private setKeywords(keywords?: string[]): void {
		const selector = 'meta[name="keywords"]'
		if (keywords?.length) {
			this.meta.updateTag({ name: 'keywords', content: keywords.join(', ') })
		} else {
			const tag = this.document.querySelector<HTMLMetaElement>(selector)
			if (tag) tag.remove()
		}
	}

	/**
   * Sets or removes the canonical link.
	 * @example <link rel="canonical" href="url...">
   */
	private setCanonicalUrl(url?: string): void {
		const selector = 'link[rel=\'canonical\']'
		const link: HTMLLinkElement | null = this.document.querySelector(selector)

		if (url) {
			if (link) link.href = url
			else {
				const newLink = this.document.createElement('link')
				newLink.rel = 'canonical'
				newLink.href = url
				this.document.head.appendChild(newLink)
			}
		} else if (link) {
			link.remove()
		}
	}

	/**
   * Sets or removes a single <script type="application/ld+json"> with the provided schema.
	 * @see https://json-ld.org/
	 * @see https://developers.google.com/search/docs/appearance/structured-data/intro-structured-data - Google’s structured data guidelines
   */
	private setJsonLd(jsonLd?: object): void {
		const script: HTMLScriptElement | null = this.document.querySelector('script[type=\'application/ld+json\']')

		if (jsonLd) {
			try {
				const jsonLdString = JSON.stringify(jsonLd)
				if (script) script.textContent = jsonLdString
				else {
					const newScript = this.document.createElement('script')
					newScript.type = 'application/ld+json'
					newScript.textContent = jsonLdString
					this.document.head.appendChild(newScript)
				}
			} catch (error) {
				this.logger.error('Failed to serialize JSON-LD:', error)
			}
		} else if (script) {
			script.remove()
		}
	}

	private getDeepestChild(route: ActivatedRoute): ActivatedRoute {
		return route.firstChild ? this.getDeepestChild(route.firstChild) : route
	}
}
